import { memo, useState } from 'react';
import { motion } from 'motion/react';
import { AlertTriangle, Check, Copy, RotateCw } from 'lucide-react';
import type { Attachment, Source, ToolActivity } from '@medicail/shared';
import { BrandMark, Button, IconButton } from '@/components/ui';
import { Markdown } from './Markdown';
import { Sources } from './Sources';
import { ToolPills } from './ToolPills';
import { MessageAttachment } from './AttachmentChip';

export interface MessageView {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments?: Attachment[];
  sources?: Source[];
  tools?: ToolActivity[];
  error?: string;
  streaming?: boolean;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      label={copied ? 'Copied' : 'Copy message'}
      size="icon-sm"
      onClick={async () => {
        if (await copyText(text)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        }
      }}
    >
      {copied ? <Check className="size-4 text-leaf-600 dark:text-green-300" /> : <Copy className="size-4" />}
    </IconButton>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1.5 py-2" role="status" aria-label="MedicAiL is thinking">
      {[0, 1, 2].map((i) => (
        <span key={i} className="size-2 animate-dot rounded-full bg-royal-400" style={{ animationDelay: `${i * 0.16}s` }} />
      ))}
    </span>
  );
}

export const MessageItem = memo(function MessageItem({
  message,
  animate = true,
  onRetry,
}: {
  message: MessageView;
  animate?: boolean;
  /** Provided on the last assistant message when it can be retried. */
  onRetry?: () => void;
}) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <motion.div initial={animate ? { opacity: 0, y: 12 } : false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="flex justify-end">
        <div className="max-w-[88%] rounded-bubble rounded-br-md bg-bubble-user px-4 py-3 text-bubble-user-ink shadow-soft sm:max-w-[75%]">
          {message.attachments && message.attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap items-start gap-2">
              {message.attachments.map((a) => (
                <MessageAttachment key={a.attachmentId} attachment={a} />
              ))}
            </div>
          )}
          {message.content && <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>}
        </div>
      </motion.div>
    );
  }

  const showTyping = message.streaming && !message.content;
  return (
    <motion.div initial={animate ? { opacity: 0, y: 12 } : false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="flex gap-3">
      <BrandMark className="mt-0.5 size-8 shrink-0" />
      <div className="min-w-0 flex-1">
        {message.streaming && <ToolPills tools={message.tools ?? []} />}
        {showTyping ? <TypingDots /> : message.content ? <Markdown streaming={message.streaming}>{message.content}</Markdown> : null}
        {message.error && !message.streaming && (
          <div role="alert" className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-600 dark:bg-red-500/10 dark:text-red-300">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1">{message.error}</span>
            {onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                <RotateCw className="size-3.5" /> Retry
              </Button>
            )}
          </div>
        )}
        {message.sources && message.sources.length > 0 && <Sources sources={message.sources} />}
        {!message.streaming && message.content && (
          <div className="-ml-2 mt-2 flex items-center gap-1">
            <CopyButton text={message.content} />
          </div>
        )}
      </div>
    </motion.div>
  );
});
